import React from 'react';
import { Trans, useTranslation, withTranslation, WithTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { H1, P } from '../../../../components/Typography';
import Modal from '../../../../components/Modal';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { distances, grid } from '../../../../styles/constants';
import { LoadingOverlay } from '../../../../components/Loading';
import { useAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { Formik } from 'formik';
import { FormattedInput, Input, ValidationState } from '../../../../components/Forms';
import { CleaveOptions } from 'cleave.js/options';
import { getValueAt } from '../../../../helpers/getValueAt';
import { getAccountNumberValidator, validate, validateRequired } from '../../../../helpers/validation';
import AccountNumber from '../../../../components/Forms/AccountNumber';
import { useActions } from '../../../../Actions';
import { useSelector } from 'react-redux';
import { ModalState, namespace } from '../actions';

interface ValidatedInputProps extends WithTranslation {
    values: any;
    initialValues: any;
    errors: any;
    path: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    touched: any;
    required?: boolean;
    prefix?: string;
    options?: CleaveOptions;
    maxlength?: number;
    formatter?: (value: string) => string;
    setFieldValue?: (field: string, value: string) => void;
    setFieldTouched?: (field: string) => void;
    country?: string;
}

const ValidatedInput = (
    {
        t,
        values,
        initialValues,
        errors,
        path,
        onChange,
        onBlur,
        touched,
        required,
        prefix,
        options,
        maxlength,
        setFieldValue,
        setFieldTouched,
        country,
    }: ValidatedInputProps
) => {
    const value = getValueAt(path, values);
    const initialValue = getValueAt(path, initialValues);
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    const validation =
        (was_touched || initialValue) && error
            ? {
                state: ValidationState.Invalid,
                message: error,
            }
            : undefined;
    const label = required
        ? `${t(`settings.payment_connections.collector_modal.fields.${path}`)} * `
        : t(`settings.payment_connections.collector_modal.fields.${path}`);
    if (path === 'account_number' && setFieldValue && setFieldTouched) {
        return (
            <InputWrapper>
                <AccountNumber
                    name={path}
                    value={value || ''}
                    setFieldValue={setFieldValue}
                    onBlur={() => {
                        setFieldTouched('account_number');
                    }}
                    validation={was_touched && validation}
                    country={country || ''}
                />
            </InputWrapper>
        );
    }

    if (options) {
        return (
            <InputWrapper>
                <FormattedInput
                    label={label}
                    placeholder={t(`settings.payment_connections.collector_modal.placeholders.${path}`)}
                    type="text"
                    name={path}
                    value={value || ''}
                    onChange={(e) => {
                        onChange(e);
                    }}
                    onBlur={(e) => {
                        onChange(e);
                        onBlur(e);
                    }}
                    validation={validation}
                    prefix={prefix}
                    options={options}
                />
            </InputWrapper>
        );
    }
    return (
        <InputWrapper>
            <Input
                label={label}
                placeholder={t(`settings.payment_connections.collector_modal.placeholders.${path}`)}
                type="text"
                name={path}
                value={value || ''}
                onChange={(e) => {
                    onChange(e);
                }}
                onBlur={(e) => {
                    onChange(e);
                    onBlur(e);
                }}
                validation={validation}
                prefix={prefix}
                maxlength={maxlength}
            />
        </InputWrapper>
    );
};

const TranslatedValidatedInput = withTranslation()(ValidatedInput);

interface FormikValueProps {
    bank_name: string;
    account_number: string;
    [key: string]: any;
}

const CollectorModal = () => {
    const { t } = useTranslation();
    const { signupForConnection, closeModal } = useActions('connections.collector');
    const { prodAccountId } = useAccountIds();

    const { modalState, account } = useSelector(state => {
        return ({
            modalState: state[namespace].modalState,
            account: state[namespace].account,
        });
    });

    if (!prodAccountId) {
        return null;
    }

    if (modalState === ModalState.Closed) {
        return null;
    }

    const validateValues = (values: FormikValueProps) => {
        let errors: { [key: string]: string } = {};
        if (!values) {
            // this should not happen but...
            return { error: 'account not loaded yet...' };
        }
        const validators = [
            {
                path: 'bank_name',
                validator: validateRequired(t('settings.payment_connections.collector_modal.invalid.bank_name')),
            },
            {
                path: 'account_number',
                validator: validateRequired(
                    t('settings.payment_connections.collector_modal.invalid.account_number.required')
                ),
            },
            {
                path: 'account_number',
                validator: getAccountNumberValidator(
                    account?.company.address.country ?? '',
                    t('settings.payment_connections.collector_modal.invalid.account_number.required')
                ),
            },
        ];

        errors = validators.reduce((acc, elem) => {
            return validate(elem.path, elem.validator, values, acc);
        }, errors);

        return errors;
    };

    if (!account) {
        return <LoadingOverlay />;
    }

    const initialFormValues: FormikValueProps = {
        bank_name: '',
        account_number: '',
    };

    const initialValidation = validateValues(initialFormValues);
    console.log('modalState', modalState);
    return (
        (<Modal fixedWidth>
            {[
                ModalState.ApplicationFormOpened,
                ModalState.InitiateApplicationLoading,
            ].includes(
                modalState
            ) && (
                <>
                    <H1>{t('settings.payment_connections.collector_modal.title_intro')}</H1>
                    <Formik
                        enableReinitialize
                        initialValues={initialFormValues}
                        initialErrors={initialValidation}
                        validate={validateValues}
                        onSubmit={(values) => {
                            if (
                                !account ||
                                !account.company ||
                                !account.company.organization_number ||
                                !account.company.website ||
                                !prodAccountId
                            ) {
                                // How could we ever get here?
                                return;
                            }
                            signupForConnection(prodAccountId, {
                                account_number: values.account_number.replace(/\s/g, ''),
                                business_name: account.company.business_name,
                                legal_name: account.company.business_name,
                                organization_number: account.company.organization_number || '',
                                bank_name: values.bank_name,
                                website: account.company.website || '',
                                payment_options: [
                                    {
                                        currencies: ['NOK'],
                                        type: 'collector.invoice',
                                    },
                                    {
                                        currencies: ['NOK'],
                                        type: 'collector.installment',
                                    },
                                ],
                            });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            touched,
                            handleSubmit,
                            isValid,
                            setFieldValue,
                            setFieldTouched,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <SpacedWrapper>
                                        <P>
                                            <Trans i18nKey="settings.payment_connections.collector_modal.intro" />
                                        </P>
                                    </SpacedWrapper>
                                    <SpacedWrapper>
                                        <TranslatedValidatedInput
                                            path="bank_name"
                                            initialValues={initialFormValues}
                                            values={values}
                                            errors={errors}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            touched={touched}
                                            required
                                        />
                                        <TranslatedValidatedInput
                                            path="account_number"
                                            initialValues={initialFormValues}
                                            values={values}
                                            errors={errors}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            touched={touched}
                                            setFieldValue={setFieldValue}
                                            setFieldTouched={setFieldTouched}
                                            country={account.company.address.country}
                                            required
                                        />
                                    </SpacedWrapper>
                                    <ButtonGroup>
                                        <Button type="submit" disabled={!isValid}>
                                            {t(
                                                'settings.payment_connections.collector_modal.sign_up_with_collector_call_to_action'
                                            )}
                                        </Button>
                                        <Button className="alt" onClick={closeModal}>
                                            {t('settings.payment_connections.collector_modal.cancel')}
                                        </Button>
                                    </ButtonGroup>
                                    {modalState ===
                                        ModalState.InitiateApplicationLoading && <LoadingOverlay />}
                                </form>
                            );
                        }}
                    </Formik>
                </>
            )}
            {modalState === ModalState.ApplicationReceived && (
                <>
                    <H1>{t('settings.payment_connections.collector_modal.title_last_step')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.collector_modal.last_step" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closeModal}>
                            {t('settings.payment_connections.collector_modal.close')}
                        </Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>)
    );
};

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;

const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;

export default CollectorModal;
